import check from '../libraries/check-types-ext';
import AppError from '../libraries/AppError';
import * as utils from '../libraries/Utils';
import { ProductSelectionType } from '../plugins/commonTypes/OrderTypes';
import { G6LineType } from './OrderLineTypesG6';
const firstval = (val1 = undefined, val2 = undefined, val3 = undefined) => {
    if (val1 !== undefined)
        return val1;
    if (val2 !== undefined)
        return val2;
    if (val3 !== undefined)
        return val3;
    else
        return undefined;
};
const firstvalString = (val1, val2 = undefined, val3 = undefined) => {
    if (typeof val1 === 'string' && val1)
        return val1;
    if (typeof val2 === 'string' && val2)
        return val2;
    if (typeof val3 === 'string' && val3)
        return val3;
    else
        return undefined;
};
export const createOrderLineNodeG6 = (params) => {
    const { type, productId, rootProductId, quantity, name, ean, plu, subLines, unitType, productIngredients, selectedOption, unselectedOption, options, discount, applicableDiscounts, includedInMainPrice = false, voucherId, addedByGame = false, } = params;
    let { price } = params;
    check.string.orFail(type, 'type');
    check.string.orFail(name, 'name');
    if (type === G6LineType.Product) {
        if (typeof price !== 'number') {
            price = 0;
        }
        if (price > 0) {
            check.string.orFail(unitType, 'unitType', params);
        }
    }
    if (type === G6LineType.Aggregation || type === G6LineType.Config) {
        check.undefined.orFail(price);
        check.undefined.orFail(unitType);
    }
    if (type === G6LineType.Aggregation) {
        check.string.orFail(rootProductId);
    }
    if (typeof price === 'number' && price > 0 && type !== G6LineType.Delivery) {
        check.string.orFail(unitType, 'unitType');
    }
    const node = {
        type,
        generation: 6,
        includedInMainPrice,
        subLines: Array.isArray(subLines) ? subLines : [],
    };
    if (productId)
        node.productId = productId;
    if (rootProductId)
        node.rootProductId = rootProductId;
    if (quantity !== undefined)
        node.quantity = quantity;
    if (price !== undefined)
        node.price = price;
    if (productIngredients)
        node.productIngredients = productIngredients;
    if (name)
        node.name = name.replace('undefined', '');
    if (unitType)
        node.unitType = unitType;
    if (ean)
        node.ean = ean;
    if (plu)
        node.plu = plu;
    if (options)
        node.options = options;
    if (selectedOption)
        node.selectedOption = selectedOption;
    if (unselectedOption)
        node.unselectedOption = unselectedOption;
    if (discount)
        node.discount = discount;
    if (applicableDiscounts)
        node.applicableDiscounts = applicableDiscounts;
    if (voucherId)
        node.voucherId = voucherId;
    if (addedByGame)
        node.addedByGame = addedByGame;
    return node;
};
const getMainProduct = (productCatalog, rootProduct, selectionRoot) => {
    const mainProductFromGroupId = rootProduct.options.mainProductFromGroupId;
    if (mainProductFromGroupId !== undefined) {
        if (!selectionRoot.groups) {
            throw AppError.MainProductMissingError().addData('rootProduct', rootProduct);
        }
        const groupSelection = selectionRoot.groups.find((g) => g.groupId === mainProductFromGroupId);
        const ps = groupSelection.selectedProducts;
        check.array.orFail(ps);
        if (ps.length !== 1) {
            throw AppError.CodingError().addData('selectedProducts', ps);
        }
        const productId = typeof ps[0] === 'string' ? ps[0] : ps[0].productId;
        return productCatalog.getProduct(productId);
    }
    else {
        if (rootProduct.options.noMainProduct)
            return undefined;
        else
            return rootProduct;
    }
};
const createConfigNode = (rootProduct, configSelection) => {
    const config = rootProduct.getConfig(configSelection.configurationId);
    const orderText = (() => {
        switch (config.appearance) {
            case 'switches': {
                const val = parseInt(configSelection.selectedValue ? configSelection.selectedValue : 0);
                check.number.orFail(val);
                check.array.of.object.orFail(config.options.options, 'configurations.options');
                const option = config.options.options.find((opt) => opt.id === val);
                check.object.orFail(option, 'option', config.options.options);
                return config.name + ': ' + option.name;
            }
            case 'textarea':
                return config.name + ': ' + configSelection.selectedValue;
            case 'checklist':
                return config.name;
        }
    })();
    let copiedOptions;
    let unselectedOption;
    const hasSelectedValue = typeof configSelection.selectedValue !== 'undefined';
    const _options = config.options.options;
    const hasOptions = Array.isArray(_options);
    const hasMultipleSelectedValues = Array.isArray(configSelection.selectedValue);
    if (hasSelectedValue && hasOptions) {
        if (hasMultipleSelectedValues) {
            const _selectedOption = _options.filter((opt, index) => {
                return configSelection.selectedValue.indexOf(index) !== -1;
            });
            const _unselectedOption = _options.filter((opt, index) => {
                return configSelection.selectedValue.indexOf(index) === -1;
            });
            copiedOptions = _selectedOption.map((op) => utils.copyObject(op));
            unselectedOption = _unselectedOption.map((op) => utils.copyObject(op));
        }
        else {
            copiedOptions = utils.copyObject(_options[configSelection.selectedValue]);
            const _unselectedOption = _options.filter((opt, index) => {
                return configSelection.selectedValue === index;
            });
            unselectedOption = _unselectedOption.map((op) => utils.copyObject(op));
        }
    }
    return createOrderLineNodeG6({
        name: orderText,
        type: G6LineType.Config,
        selectedOption: copiedOptions,
        unselectedOption: unselectedOption || undefined,
    });
};
const createGroupNodes = (productCatalog, rootProduct, groupSelection, selectionRoot, rootProductId, buildSubSelection) => {
    if (!check.array.of.string(groupSelection.selectedProducts) &&
        !check.array.of.object(groupSelection.selectedProducts)) {
        throw AppError.CodingError('Expected array of numbers or objects').addData('selectedProducts', groupSelection.selectedProducts);
    }
    const myGroup = rootProduct.getGroup(groupSelection.groupId);
    const productValueObjects = groupSelection.selectedProducts.map((selectedProduct) => {
        const { productId, quantity } = (() => {
            if (check.string(selectedProduct)) {
                return { productId: selectedProduct, quantity: undefined };
            }
            else {
                check.string.orFail(selectedProduct.productId);
                return { productId: selectedProduct.productId, quantity: selectedProduct.quantity };
            }
        })();
        if (typeof selectedProduct === 'object' &&
            ((Array.isArray(selectedProduct.groups) && selectedProduct.groups.length > 0) ||
                (Array.isArray(selectedProduct.configurations) && selectedProduct.configurations.length > 0))) {
            if (typeof selectedProduct.quantity === 'undefined') {
                selectedProduct.quantity = selectionRoot.quantity;
            }
            return { orderLineNode: buildSubSelection(selectedProduct) };
        }
        const pdFromCatalog = productCatalog.getProduct(productId);
        const pdFromGroup = myGroup.products.find((p) => p.id_ref === productId);
        const priceInGroup = (() => {
            if (pdFromGroup) {
                const prFromGrp = parseFloat(pdFromGroup.productPriceInGroup);
                if (!isNaN(prFromGrp)) {
                    return prFromGrp;
                }
            }
            return pdFromCatalog.priceStructure.priceInGroup;
        })();
        const productName = (() => {
            var _a;
            if (((_a = groupSelection.options) === null || _a === void 0 ? void 0 : _a.withTextArea) && selectedProduct.textValue) {
                return `${pdFromCatalog.name} (${selectedProduct.textValue})`;
            }
            return pdFromCatalog.name;
        })();
        return {
            id: productId,
            name: productName,
            price: priceInGroup,
            unit: pdFromCatalog.unit,
            ean: pdFromCatalog.ean,
            plu: pdFromCatalog.plu,
            quantity: quantity,
            options: myGroup.options,
        };
    });
    const coef = myGroup.options.countPerAmount && selectionRoot && selectionRoot.quantity ? selectionRoot.quantity : 1;
    let freeQuantityCount = myGroup.options.freeQuantity !== undefined ? parseInt('' + myGroup.options.freeQuantity) * coef : 0;
    let overFreeQuantityCount = 0;
    const subLines = productValueObjects.reduce((pv, vo) => {
        if (vo.orderLineNode) {
            return [...pv, vo.orderLineNode];
        }
        else {
            const data = {
                productId: vo.id,
                rootProductId: rootProduct.id,
                name: vo.name,
            };
            if (myGroup.options.freeQuantity !== undefined &&
                myGroup.options.maxQuantity !== undefined &&
                myGroup.options.freeQuantity === myGroup.options.maxQuantity &&
                myGroup.options.freeQuantity === myGroup.products.length) {
                data.type = G6LineType.Config;
                if (typeof vo.quantity === 'number') {
                    data.quantity = vo.quantity;
                }
            }
            else {
                data.type = G6LineType.Product;
                data.options = vo.options;
                data.price = firstval(vo.price);
                data.unitType = firstvalString(vo.unit, myGroup.unit, rootProduct.unit);
                data.ean = firstval(myGroup.ean, vo.ean);
                data.plu = firstval(myGroup.plu, vo.plu);
                data.quantity = firstval(vo.quantity, selectionRoot.quantity);
                if (freeQuantityCount > 0) {
                    data.includedInMainPrice = true;
                    const usedQty = myGroup.options.countPerAmount ? data.quantity : 1;
                    if (freeQuantityCount < usedQty) {
                        overFreeQuantityCount = usedQty - freeQuantityCount;
                        data.quantity = freeQuantityCount;
                        freeQuantityCount = 0;
                    }
                    else {
                        freeQuantityCount = freeQuantityCount - usedQty;
                    }
                }
                else {
                    data.includedInMainPrice = false;
                }
            }
            if (overFreeQuantityCount > 0) {
                const extraData = Object.assign({}, data);
                extraData.includedInMainPrice = false;
                extraData.quantity = overFreeQuantityCount;
                overFreeQuantityCount = 0;
                return [...pv, createOrderLineNodeG6(data), createOrderLineNodeG6(extraData)];
            }
            return [...pv, createOrderLineNodeG6(data)];
        }
    }, []);
    if (subLines.length === 1 && !rootProduct.options.noMainProduct) {
        if (myGroup.products && myGroup.products.length > 1) {
            const emptyValues = [null, undefined, '', ' '];
            const prefix = emptyValues.includes(myGroup.name) ? '' : `${myGroup.name} - `;
            subLines[0].name = prefix + subLines[0].name;
        }
        return subLines[0];
    }
    else if (subLines.length > 0) {
        return createOrderLineNodeG6({
            name: myGroup.name,
            type: (() => {
                if (subLines[0].type === G6LineType.Config) {
                    return G6LineType.Config;
                }
                else if ([G6LineType.Product, G6LineType.Aggregation].includes(subLines[0].type)) {
                    return G6LineType.Aggregation;
                }
                else {
                    throw AppError.CodingError('Should not happen');
                }
            })(),
            rootProductId: rootProduct.id,
            subLines: subLines,
        });
    }
    return null;
};
const buildSelectionTree = (productCatalog, selectionRoot, rootProductId, includeDisabledProducts, isSubline) => {
    const rootProduct = selectionRoot.type !== ProductSelectionType.Config
        ? productCatalog.getProduct(selectionRoot.productId, includeDisabledProducts)
        : undefined;
    let subLinesForMainProduct = [];
    let subLinesForAddons = [];
    const buildSubSelection = (selection) => {
        return buildSelectionTree(productCatalog, selection, rootProductId, false, true)[0];
    };
    if (selectionRoot.groups) {
        const predicate = (g) => {
            const groupObj = rootProduct.getGroup(g.groupId);
            return groupObj && groupObj.options.useInGroupOnly;
        };
        const predicate2 = (g) => {
            if (rootProduct.options.mainProductFromGroupId !== undefined)
                return g.groupId !== rootProduct.options.mainProductFromGroupId;
            else
                return true;
        };
        const groupsConfiguringMainProduct = selectionRoot.groups.filter((g) => predicate(g) && predicate2(g));
        const groupsRepresentingAddons = selectionRoot.groups.filter((g) => !predicate(g) && predicate2(g));
        subLinesForMainProduct = groupsConfiguringMainProduct
            .map((groupSelection) => createGroupNodes(productCatalog, rootProduct, groupSelection, selectionRoot, rootProductId, buildSubSelection))
            .filter((subLine) => subLine !== null && typeof subLine === 'object');
        subLinesForAddons = groupsRepresentingAddons
            .map((groupSelection) => createGroupNodes(productCatalog, rootProduct, groupSelection, selectionRoot, rootProductId, buildSubSelection))
            .filter((subLine) => subLine !== null && typeof subLine === 'object');
        check.array.orFail(subLinesForMainProduct);
    }
    const subLinesForConfiguration = selectionRoot.configurations
        ? selectionRoot.configurations.map((configSelection) => createConfigNode(rootProduct, configSelection))
        : [];
    const mainProduct = rootProduct ? getMainProduct(productCatalog, rootProduct, selectionRoot) : undefined;
    const result = [];
    if (mainProduct) {
        check.string.orFail(mainProduct.unit, 'unitType');
        const price = isSubline ? mainProduct.priceStructure.priceInGroup : mainProduct.price;
        const extraSubLines = subLinesForConfiguration;
        const mainLine = createOrderLineNodeG6({
            type: G6LineType.Product,
            productId: mainProduct.id,
            rootProductId: rootProductId,
            quantity: selectionRoot.quantity,
            discount: isSubline ? undefined : rootProduct.discount,
            applicableDiscounts: isSubline ? undefined : rootProduct.applicableDiscounts,
            voucherId: selectionRoot.voucherId,
            name: mainProduct.name,
            options: rootProduct.options,
            ean: mainProduct.ean,
            plu: mainProduct.plu,
            addedByGame: selectionRoot.addedByGame,
            price,
            unitType: mainProduct.unit,
            subLines: [...extraSubLines, ...subLinesForMainProduct],
        });
        result.push(mainLine);
    }
    subLinesForAddons.forEach((line) => {
        if (line.name === undefined || line.name === '') {
            if (line.subLines) {
                line.subLines.forEach((subline) => {
                    result.push(subline);
                });
            }
        }
        else {
            result.push(line);
        }
    });
    if (result.length > 1 || (rootProduct && rootProduct.options.noMainProduct && result.length > 0)) {
        check.string.orFail(rootProduct.id);
        const aggregation = createOrderLineNodeG6({
            type: G6LineType.Aggregation,
            name: rootProduct.name,
            discount: rootProduct.discount,
            applicableDiscounts: rootProduct.applicableDiscounts,
            quantity: selectionRoot.quantity,
            options: rootProduct.options,
            addedByGame: selectionRoot.addedByGame,
            rootProductId: rootProductId ? rootProductId : rootProduct.id,
            subLines: [...result, ...subLinesForConfiguration],
        });
        return [aggregation];
    }
    else
        return result;
};
export const buildOrderLineTreeG6 = ({ productCatalog, productSelection, options = {}, session, }) => {
    check.object.orFail(options, 'options');
    check.array.of.object.orFail(productSelection, 'selections');
    const orderLines = [];
    productSelection.forEach((selection) => {
        buildSelectionTree(productCatalog, selection, selection.productId, !!options.includeDisabledProducts, false).forEach((line) => {
            orderLines.push(line);
        });
    });
    return orderLines;
};
